/*
  Pricing rules of the personalization form (snippets/personalization-form.liquid): which variant (SKU, price)
  a set of items with given sub-item counts maps to, how many sub-items a piece may have in each set size, and
  what a ?variant= link preselects. Pure functions of the product's personalization_config and its variants,
  so the same file runs in the browser (window.ptPricing) and in the unit tests (tests/unit, Node).

  create(config, variants) -> {
    perItem, subMin(), subMax(total), pack(count), bySku(sku), variantFor(count, subCounts),
    itemVariant(subCount), validTotal(total), fromPrice(), preselect(sku)
  }
*/
(function (root) {
  function create(config, variants) {
    const sub = config.sub_items;
    const perItem = !!config.item_count;
    const packs = (config.packs && config.packs.options) || [];

    const subMin = () => Number(sub.min || 1);

    // Most sub-items per item for this set size: a pack may set a lower sub_max than sub_items.max.
    const subMax = (total) => {
      const found = perItem ? null : pack(total);
      return Math.min(Number(sub.max), Number((found && found.sub_max) || sub.max));
    };

    const pack = (count) => packs.find((entry) => Number(entry.count) === count);

    const bySku = (sku) => (sku ? variants.find((variant) => variant.sku === sku) : null) || null;

    // Pack mode: variant of the whole set.
    const variantFor = (count, subCounts) => {
      const found = pack(count);
      if (!found) return null;
      // 套装按加价档总数选 SKU：每件的子项数达到 extra_at 里几个门槛就算几档（例如第 2、3 个头像各一档）
      if (found.sku_by_extra) {
        const steps = (found.extra_at || []).map(Number);
        const extra = subCounts.reduce((sum, n) => sum + steps.filter((step) => Number(n) >= step).length, 0);
        return bySku(found.sku_by_extra[String(extra)]);
      }
      return bySku(found.sku_by_sub_count ? found.sku_by_sub_count[String(subCounts[0])] : String(found.sku).replace('{sub_count}', String(subCounts[0])));
    };

    // Price of one item on its own (per-item line, or the single pack used for savings).
    const itemVariant = (subCount) => {
      if (perItem) return bySku(config.item_variants.sku_by_sub_count[String(subCount)]);
      return variantFor(1, [subCount]);
    };

    const validTotal = (total) => (perItem
      ? total >= Number(config.item_count.min || 1) && total <= Number(config.item_count.max)
      : !!pack(total));

    // Lowest price a shopper can pay ("From $59.00"), in cents; null when nothing is priced.
    const fromPrice = () => {
      const prices = [];
      for (let n = subMin(); n <= Number(sub.max); n += 1) {
        const variant = itemVariant(n);
        if (variant) prices.push(variant.price);
      }
      packs.forEach((entry) => {
        const count = Number(entry.count);
        const variant = variantFor(count, Array(count).fill(subMin()));
        if (variant) prices.push(variant.price);
      });
      return prices.length ? Math.min(...prices) : null;
    };

    // ?variant= link: the set size and sub-item counts that give this SKU, or null.
    //   { count, initialSubCount }  -> only the first piece gets that count (for example PP-SINGLE-2 = 1 piece, 2 portraits)
    //   { count, initialSubCounts } -> one count per piece, when the add-on steps don't fit on the first piece
    const preselect = (sku) => {
      if (perItem || !sku) return null;
      const preferred = Number(sub.default || subMin());
      for (const entry of packs) {
        const count = Number(entry.count);
        const subCounts = [];
        for (let n = subMin(); n <= subMax(count); n += 1) {
          const variant = variantFor(count, [n]);
          if (variant && variant.sku === sku) subCounts.push(n);
        }
        if (!subCounts.length) continue;
        // 一个 SKU 对应几种数量时（例如爪印 1–3 个同价），优先用默认数量
        return { count, initialSubCount: subCounts.includes(preferred) ? preferred : subCounts[0] };
      }
      // 套装加价档多到第 1 件放不下时（例如 2 件共 5 个头像）：从第 1 件起逐件加到对应档数，让显示价等于链接的变体价
      const found = packs.find((entry) => Object.values(entry.sku_by_extra || {}).includes(sku));
      if (!found) return null;
      const steps = (found.extra_at || []).map(Number).sort((a, b) => a - b);
      const base = steps.some((step) => preferred >= step) ? subMin() : preferred;
      let extra = Number(Object.keys(found.sku_by_extra).find((key) => found.sku_by_extra[key] === sku));
      return {
        count: Number(found.count),
        initialSubCounts: Array.from({ length: Number(found.count) }, () => {
          const take = Math.min(extra, steps.length);
          extra -= take;
          return take ? steps[take - 1] : base;
        }),
      };
    };

    return { perItem, subMin, subMax, pack, bySku, variantFor, itemVariant, validTotal, fromPrice, preselect };
  }

  root.ptPricing = { create };
  if (typeof module === 'object' && module.exports) module.exports = { create };
})(typeof window !== 'undefined' ? window : globalThis);
